/**
 * Quick add-to-cart for `.variant-pill` buttons — Horizon theme (2026+).
 *
 * Why this is faster than a manual /cart/add.js + section-fetch flow:
 * Shopify shipped a standard storefront actions/events layer in the Spring '26
 * Edition (June 2026). `Shopify.actions.updateCart()` and `Shopify.actions.openCart()`
 * are injected on every Liquid storefront and, on themes that implement them
 * (Horizon does), call straight into the theme's own optimised cart-drawer
 * rendering path — a single network request, a targeted DOM morph instead of an
 * innerHTML replace, and the drawer's own open/animation logic. That's exactly
 * the machinery Horizon's native "Add to cart" buttons use, so this piggybacks
 * on it instead of re-implementing a slower version by hand.
 *
 * Falls back to the classic Ajax Cart API for storefronts/themes where the
 * standard actions aren't available yet.
 *
 * This version does NOT open the cart drawer on add — it only refreshes the
 * cart count/bubble, since updateCart()'s standard event is enough for that.
 */
document.addEventListener('click', async function (event) {
  const button = event.target.closest('.variant-pill');
  if (!button) return;

  event.preventDefault();

  const variantId = button.dataset.variantId;
  if (!variantId) return;

  button.classList.add('loading');

  try {
    if (window.Shopify?.actions?.updateCart && window.Shopify?.actions?.openCart) {
      await addViaStandardActions(variantId);
    } else {
      await addViaLegacyAjaxCart(variantId);
    }
    triggerAddedTick(button);
  } catch (error) {
    console.error('Quick add error:', error);
  } finally {
    button.classList.remove('loading');
  }
});

/**
 * Map of button -> pending reset timeout, so spam-clicking the same pill
 * restarts the animation cleanly instead of stacking timeouts.
 * @type {WeakMap<Element, number>}
 */
const tickResetTimeouts = new WeakMap();

/**
 * Injects a checkmark tick into the button (once) and briefly shows it,
 * matching Horizon's own add-to-cart button animation pattern
 * (a data-added attribute driving a CSS animation), scoped to variant pills.
 * Pair this with the accompanying CSS (see chat) for the actual animation.
 * @param {HTMLElement} button
 */
function triggerAddedTick(button) {
  if (!button.querySelector('.variant-pill__tick')) {
    button.insertAdjacentHTML(
      'beforeend',
      `<svg class="variant-pill__tick" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 12.5L9.5 18L20 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
      </svg>`
    );
  }

  const existingTimeout = tickResetTimeouts.get(button);
  if (existingTimeout) clearTimeout(existingTimeout);

  // Restart the animation even on rapid repeat clicks.
  button.classList.remove('is-added');
  // Force reflow so re-adding the class re-triggers the CSS animation.
  void button.offsetWidth;
  button.classList.add('is-added');

  const timeoutId = setTimeout(() => {
    button.classList.remove('is-added');
    tickResetTimeouts.delete(button);
  }, 1100);

  tickResetTimeouts.set(button, timeoutId);
}

/**
 * Preferred path: Shopify's standard storefront actions.
 * @param {string} variantId
 */
async function addViaStandardActions(variantId) {
  const merchandiseId = variantId.startsWith('gid://')
    ? variantId
    : `gid://shopify/ProductVariant/${variantId}`;

  const { userErrors } = await window.Shopify.actions.updateCart({
    lines: [{ merchandiseId, quantity: 1 }],
  });

  if (userErrors?.length) {
    throw new Error(userErrors.map((e) => e.message).join(', '));
  }

  // Intentionally not calling Shopify.actions.openCart() — updateCart() alone
  // already emits the standard cart-update event, which is what Horizon's
  // header cart icon/bubble listens to for its count. No drawer pop-open.
}

/**
 * Fallback for themes without the standard actions layer.
 * Combines the add + section refresh into a single request (the slow part of
 * the old approach was doing these as two sequential fetches), and morphs only
 * the drawer's inner content instead of clobbering the whole innerHTML.
 * @param {string} variantId
 */
async function addViaLegacyAjaxCart(variantId) {
  const cartDrawer = document.querySelector('cart-drawer-component, cart-drawer, #cart-drawer');
  const sectionId =
    cartDrawer?.dataset?.sectionId ||
    document.querySelector('cart-items-component[data-drawer]')?.dataset.sectionId ||
    'cart-drawer';

  const response = await fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({
      items: [{ id: variantId, quantity: 1 }],
      sections: sectionId,
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) {
    throw new Error('Could not add item to cart');
  }

  const result = await response.json();
  const sectionHtml = result.sections?.[sectionId];

  if (sectionHtml && cartDrawer) {
    const newDoc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newInner = newDoc.querySelector(`[data-section-id="${sectionId}"]`) || newDoc.body.firstElementChild;

    // Prefer a real morph if the theme's own morph helper is on the page
    // (avoids a full innerHTML replace + reflow of the drawer contents).
    const themeMorph = window.Theme?.morph;
    if (typeof themeMorph === 'function' && newInner) {
      themeMorph(cartDrawer, newInner);
    } else if (newInner) {
      cartDrawer.innerHTML = newInner.innerHTML;
    }
  }

  // Bubble/count only — no drawer open. Horizon's header cart icon listens
  // for this to refresh its number.
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: { cart: result },
    })
  );
}